import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'

import Field from '../../../../../components/Form/Field'
import EditForm from '../../../../../features/components/Forms/EditForm'

import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'

import { filterObjectEmptyValues } from '../../../../../helpers/common'

import { updateCampaign, clearUpdateCampaign } from '../../../../../modules/actions/campaigns'
import {
  campaignUpdateErrorSelector,
  campaignUpdateIsLoadingSelector,
  campaignWasUpdatedSelector
} from '../../../../../modules/selectors/campaigns'
import { selectedTikTokAdAccountSelector } from '../../../../../modules/selectors/adAccounts'

import { formatNumber } from '../../../../../helpers/numbers'

import { TIKTOK_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../constants/changeHistory'
import { BUDGET } from '../../../../Tiktok/LineItemForms/LineItemTikTokEdit/fields'
import { getBudgetValidation } from './validation'

const TikTokCampaignBudgetSectionForm = ({
  campaign,
  providedAdAccountId,
  updateType,
  remainingBudget,
  title,
  ...formProps
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const campaignWasUpdated = useSelector(campaignWasUpdatedSelector)
  const { id: selectedTikTokAdAccountId, currency_symbol: currencySymbol } = useSelector(
    selectedTikTokAdAccountSelector
  )

  const adAccountId = providedAdAccountId || selectedTikTokAdAccountId

  const initialBudget = campaign[BUDGET]

  const handleSubmit = useCallback(
    values => {
      const updateCampaignData = {
        ...filterObjectEmptyValues(campaign),
        [BUDGET]: values[BUDGET],
        account: adAccountId,
        meta: {
          [HISTORY_META_EDIT_SECTION]: title
        }
      }

      // we use PUT for update TikTok campaign
      dispatch(
        updateCampaign(updateCampaignData, campaign.id, TIKTOK_PLATFORM, {
          updateType,
          requestMethod: 'PUT'
        })
      )
    },
    [campaign, adAccountId, title, updateType, dispatch]
  )

  const initialValues = useMemo(
    () => ({
      [BUDGET]: initialBudget || ''
    }),
    [initialBudget]
  )

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: getBudgetValidation({
      remainingBudget,
      initialBudget
    }),
    onSubmit: handleSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const handleClearUpdateCampaign = useCallback(() => {
    dispatch(clearUpdateCampaign())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={campaignWasUpdated}
      clearEditItem={handleClearUpdateCampaign}
      errorSelector={campaignUpdateErrorSelector}
      isLoadingSelector={campaignUpdateIsLoadingSelector}
      {...formProps}
    >
      <p>{t('A total budget for the whole campaign.')}</p>
      {remainingBudget && (
        <p>{t(`Your remaining budget is ${currencySymbol}` + formatNumber(remainingBudget, { max: 2 }))}</p>
      )}
      <p>{t('TikTok will allocate your budget across all your Ad Sets based on performance.')}</p>
      <Field
        type="number"
        formik={purifiedFormik}
        name={BUDGET}
        placeholder="Lifetime Budget"
        symbol={currencySymbol}
        autoComplete="off"
      />
    </EditForm>
  )
}

export default TikTokCampaignBudgetSectionForm
